import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import moment from "moment-timezone";
import classNames from "classnames";
import { Card, CardHeader, CardBody } from "reactstrap";
import { useTaskListApi } from "./api";
import { useSessionFilters } from "../../../../components/NTable/hook/useSessionFilters";
import "./style.scss";
import { isTaskListSectionVisible, mergeTaskListConfigWithDefaults } from "./config";
import { useAuthentication } from "../../../Authentication/context";
import { useAccess, useModuleAccess } from "../../../../utils/hooks/access";
import { canRequest } from "../../../Request/permissions";
import { useConfigurationModule } from "../../../Settings/Configuration/hooks/useConfigurationModule";

const exceptionRows = [
	["cameEarly", "came-early"],
	["cameLate", "came-late"],
	["leftEarly", "left-early"],
	["leftLate", "left-late"],
	["notClocked", "not-clocked"],
	["notScheduled", "not-scheduled"],
	["longBreak", "long-break"],
	["shortBreak", "short-break"],
];

const getClockExceptionPermission = (permissions, key) => {
	const permission = permissions?.[key];
	if (typeof permission === "boolean") {
		return permission;
	}

	return ["longBreak", "shortBreak"].includes(key) && permissions?.total;
};

function useTaskListVisibility() {
	const { access: taskListPermissions } = useModuleAccess("dashboard.taskList");

	const companyConfig = useConfigurationModule("dashboard.taskList");
	const config = mergeTaskListConfigWithDefaults(companyConfig);

	const { accessMap } = useAuthentication();
	const canViewSchedule = canRequest(accessMap, { namespace: "schedule", action: "canView", team: true });
	const canViewPto = canRequest(accessMap, { namespace: "pto", action: "canView", team: true });
	const canViewClock = canRequest(accessMap, { namespace: "clock", action: "canView", team: true });
	const canViewAvailability = canRequest(accessMap, { namespace: "availability", action: "canView", team: true });
	const canViewTimesheet = (
		canRequest(accessMap, { namespace: "timesheet", type: "meal-waive", action: "canView", team: true })
		|| canRequest(accessMap, { namespace: "timesheet", type: "miss-punch", action: "canView", team: true })
	);
	const { hasAccess: canViewClockExceptions } = useAccess("timeSheet.canViewClockExceptions");
	return {
		timesheetsNeedingApproval: {
			team: config.timesheetsNeedingApproval.team && taskListPermissions?.timesheetApproval?.team,
			weekly: config.timesheetsNeedingApproval.weekly && taskListPermissions?.timesheetApproval?.weekly,
		},
		missedPunches: {
			summary: config.missedPunches.summary && taskListPermissions?.missedPunches?.canView,
		},
		requestsToReview: {
			schedule: config.requestsToReview.schedule && taskListPermissions?.requests?.schedule && canViewSchedule,
			pto: config.requestsToReview.pto && taskListPermissions?.requests?.pto && canViewPto,
			clock: config.requestsToReview.clock && taskListPermissions?.requests?.clock && canViewClock,
			availability: config.requestsToReview.availability && taskListPermissions?.requests?.availability && canViewAvailability,
			timesheet: config.requestsToReview.timesheet && taskListPermissions?.requests?.timesheet && canViewTimesheet,
		},
		overtime: {
			summary: config.overtime.summary && taskListPermissions?.overtime?.canView,
		},
		mealViolations: {
			team: config.mealViolations.team && taskListPermissions?.mealViolations?.team,
		},
		clockExceptions: Object.fromEntries(
			[["total"], ...exceptionRows].map(([key]) => [key, config.clockExceptions[key]
				&& getClockExceptionPermission(taskListPermissions?.clockExceptions, key)
				&& canViewClockExceptions]),
		),
	};
}

function TaskList() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { hasAccess: canViewTaskList } = useAccess("dashboard.taskList.canView");
	const [, onScheduleRequestFilterChange] = useSessionFilters("scheduleRequests");
	const [, onClockRequestFilterChange] = useSessionFilters("clockRequests");
	const [, onAvailibilityRequestFilterChange] = useSessionFilters("availabilityRequests");
	const [, onPtoRequestFilterChange] = useSessionFilters("ptoRequests");
	const [, onTimesheetRequestFilterChange] = useSessionFilters("timesheetRequests");
	const {
		nonApprovedTimesheets,
		missedPunches,
		pendingRequests,
		overtime,
		mealViolation,
		clockExceptions,
		fetch,
		loading,
	} = useTaskListApi();

	const onClick = useCallback(
		(type) => {
			const from = moment().startOf("week").format("YYYY-MM-DD");
			const to = moment().endOf("week").format("YYYY-MM-DD");
			const requestFilters = {
				pagination: {
					pageIndex: 0,
					pageSize: 20
				},
				columnFilters: [
					{
						name: "status",
						method: "exact",
						value: "pending"
					}
				],
				sorting: []
			};

			switch (type) {
				case "timesheet-weekly":
					return navigate(
						`/time-sheet/weekly?mode=week&from=${from}&to=${to}&filter[status][exact]=unauthorized`,
					);
				case "timesheet-team": {
					return navigate(
						`/time-sheet/team?mode=week&from=${from}&to=${to}&filter[status][exact]=unauthorized`,
					);
				}
				case "schedule":
					onScheduleRequestFilterChange(requestFilters);
					return navigate(`/request/${type}`);
				case "clock":
					onClockRequestFilterChange(requestFilters);
					return navigate(`/request/${type}`);
				case "availability":
					onAvailibilityRequestFilterChange(requestFilters);
					return navigate(`/request/${type}`);
				case "pto":
					onPtoRequestFilterChange(requestFilters);
					return navigate(`/request/${type}`);
				case "timesheet":
					onTimesheetRequestFilterChange(requestFilters);
					return navigate(`/request/${type}`);
				case "missed-punches":
					return navigate(
						`/time-sheet/team?mode=week&from=${from}&to=${to}&filter[status][exact]=misspunch`,
					);
				case "meal-timesheet-team": {
					return navigate(
						`/time-sheet/team?mode=week&from=${from}&to=${to}&filter[status][exact]=meal-violations`,
					);
				}
				case "overtime":
					return navigate(
						`/time-sheet/team?mode=week&from=${from}&to=${to}&filter[hourType][exact]=overtime`,
					);
				case "clock-exceptions":
					return navigate(
						`/time-sheet/team?mode=week&from=${from}&to=${to}&filter[exceptions][exact]=any`,
					);
				case "cameEarly":
				case "cameLate":
				case "leftEarly":
				case "leftLate":
				case "notClocked":
				case "notScheduled":
				case "longBreak":
				case "shortBreak":
					return navigate(
						`/time-sheet/team?mode=week&from=${from}&to=${to}&filter[exceptions][exact]=${type}`,
					);
				default:
					return navigate("/");
			}
		},
		[navigate, onScheduleRequestFilterChange, onClockRequestFilterChange, onAvailibilityRequestFilterChange, onPtoRequestFilterChange, onTimesheetRequestFilterChange],
	);

	useEffect(() => {
		if (!canViewTaskList) return undefined;

		const controller = new AbortController();
		fetch(controller);
		return () => controller.abort();
	}, [fetch, canViewTaskList]);

	const visibleLines = useTaskListVisibility();
	const visibleExceptionsTotal = useMemo(() => exceptionRows.reduce(
		(total, [key]) => (visibleLines.clockExceptions[key] ? total + clockExceptions[key] : total),
		0,
	), [visibleLines.clockExceptions, clockExceptions]);

	if (!canViewTaskList) return null;

	return (
		<Card style={{ height: "calc(100% - 30px)" }} className="task-list h-100">
			{loading ? (
				<div className="w-100 h-100 d-flex justify-content-center align-items-center">
					<div
						className="spinner spinner-border spinner-border-sm"
						style={{ height: "30px", width: "30px" }}
						role="status"
					/>
				</div>
			) : (
				<>
					<CardHeader className="d-flex flex-column  align-items-start border-0">
						<h3 className="mb-3">{t("what-should-work-on")}</h3>
						<div className="text-sm text-primary font-weight-bolder border-bottom border-primary">
							{t("task-list")}
						</div>
					</CardHeader>

					<CardBody
						className="py-0 dashboard-task-list-scroll"
						style={{ overflow: "auto" }}
					>
						{isTaskListSectionVisible(visibleLines.timesheetsNeedingApproval) && (
							<div className="d-flex flex-column border-bottom align pb-2">
								<div className="d-flex m-0">
									<span>
										<i className="fa-solid fa-calendar-check text-primary text-lg mr-3 text-info" />
									</span>
									<span className="mb-1">
										{t("timesheets-that-need-approval")}
									</span>
								</div>

								<div className="d-flex">
									<div style={{ width: "30px" }} />
									<div className="d-flex flex-column">
										{visibleLines.timesheetsNeedingApproval.team && (
											<span
												className={classNames("text-sm text-info font-weight-bold mb-1", nonApprovedTimesheets.team > 0 && "cursor-pointer")}
												onClick={nonApprovedTimesheets.team > 0
													? () => onClick("timesheet-team")
													: undefined
												}
											>
												{t("check-team-timesheets", { count: nonApprovedTimesheets.team || 0 })}{" "}
												<i className="fa-solid fa-greater-than text-xs ml-2" />
											</span>
										)}

										{visibleLines.timesheetsNeedingApproval.weekly && (
											<span
												className={classNames("text-sm text-primary font-weight-bold", nonApprovedTimesheets.weekly > 0 && "cursor-pointer")}
												onClick={nonApprovedTimesheets.weekly > 0
													? () => onClick("timesheet-weekly")
													: undefined
												}
											>
												{t("check-weekly-timesheets", { count: nonApprovedTimesheets.weekly || 0 })}{" "}
												<i className="fa-solid fa-greater-than text-xs ml-2" />
											</span>
										)}
									</div>
								</div>
							</div>
						)}

						{isTaskListSectionVisible(visibleLines.missedPunches) && (
							<div className="d-flex border-bottom align-items-center py-2">
								<div>
									<i className="fa-solid fa-pen text-primary text-lg mr-3 text-red" />
								</div>

								<div className="d-flex flex-column">
									<span className="mb-1">
										{t("missed-punches")}
									</span>

									{visibleLines.missedPunches.summary && (
										<span
											className={classNames("text-sm text-red font-weight-bold", missedPunches > 0 && "cursor-pointer")}
											onClick={missedPunches > 0 ? () => onClick("missed-punches") : undefined}
										>
											{t(
												"check-missed-punches",
												{
													missedPunches,
												},
											)}{" "}
											<i className="fa-solid fa-greater-than text-xs ml-2" />
										</span>
									)}
								</div>
							</div>
						)}

						{isTaskListSectionVisible(visibleLines.requestsToReview) && (
							<div className="d-flex flex-column border-bottom align py-2">
								<div className="d-flex m-0">
									<span>
										<i className="fa-solid fa-check text-warning text-lg mr-3" />
									</span>
									<span className="mb-1">
										{t("requests-to-review")}
									</span>
								</div>

								<div className="d-flex">
									<div style={{ width: "30px" }} />
									<div className="d-flex flex-column">
										{visibleLines.requestsToReview.schedule && (
											<span
												className={classNames("text-sm text-warning font-weight-bold mb-1", pendingRequests?.schedule > 0 && "cursor-pointer")}
												onClick={pendingRequests?.schedule > 0 ? () => onClick("schedule") : undefined}
											>
												{t("schedule-request")}{" " + (pendingRequests?.schedule || 0)}
												<i className="fa-solid fa-greater-than text-xs ml-2" />
											</span>
										)}

										{visibleLines.requestsToReview.pto && (
											<span
												className={classNames("text-sm text-dark font-weight-bold mb-1", pendingRequests?.pto > 0 && "cursor-pointer")}
												onClick={pendingRequests?.pto > 0 ? () => onClick("pto") : undefined}
											>
												{t("pto-request")}{" " + (pendingRequests?.pto || 0)}
												<i className="fa-solid fa-greater-than text-xs ml-2" />
											</span>
										)}

										{visibleLines.requestsToReview.clock && (
											<span
												className={classNames("text-sm font-weight-bold mb-1", pendingRequests?.clock > 0 && "cursor-pointer")}
												onClick={pendingRequests?.clock > 0 ? () => onClick("clock") : undefined}
											>
												{t("clock-request")}{" " + (pendingRequests?.clock || 0)}
												<i className="fa-solid fa-greater-than text-xs ml-2" />
											</span>
										)}

										{visibleLines.requestsToReview.availability && (
											<span
												className={classNames("text-sm text-green font-weight-bold mb-1", pendingRequests?.availability > 0 && "cursor-pointer")}
												onClick={pendingRequests?.availability > 0 ? () => onClick("availability") : undefined}
											>
												{t("availability-request")}{" " + (pendingRequests?.availability || 0)}
												<i className="fa-solid fa-greater-than text-xs ml-2" />
											</span>
										)}

										{visibleLines.requestsToReview.timesheet && (
											<span
												className={classNames("text-sm text-blue font-weight-bold mb-1", pendingRequests?.timesheet > 0 && "cursor-pointer")}
												onClick={pendingRequests?.timesheet > 0 ? () => onClick("timesheet") : undefined}
											>
												{t("timesheet-request")}{" " + (pendingRequests?.timesheet || 0)}
												<i className="fa-solid fa-greater-than text-xs ml-2" />
											</span>
										)}
									</div>
								</div>
							</div>
						)}

						{isTaskListSectionVisible(visibleLines.overtime) && (
							<div className="d-flex border-bottom align-items-center py-2">
								<div>
									<i
										className="fa-solid fa-clock text-lg mr-3"
										style={{ color: "#ef3dd8" }}
									/>
								</div>

								<div className="d-flex flex-column">
									<span className="mb-1">
										{t("timesheets-with-overtime")}
									</span>

									{visibleLines.overtime.summary && (
										<span
											className={classNames("text-sm font-weight-bold", overtime > 0 && "cursor-pointer")}
											style={{ color: "#ef3dd8" }}
											onClick={overtime > 0 ? () => onClick("overtime") : undefined}
										>
											{t("check-overtime-timesheets", {
												overtime,
											})}
											<i className="fa-solid fa-greater-than text-xs ml-2" />
										</span>
									)}
								</div>
							</div>
						)}

						{isTaskListSectionVisible(visibleLines.mealViolations) && (
							<div className="d-flex flex-column border-bottom align pb-2">
								<div className="d-flex m-0">
									<span>
										<i className="fa-solid fa-calendar-check text-primary text-lg mr-3 text-dark" />
									</span>
									<span className="mb-1">
										{t("meal-violations")}
									</span>
								</div>

								<div className="d-flex">
									<div style={{ width: "30px" }} />
									<div className="d-flex flex-column">
										{visibleLines.mealViolations.team && (
											<span
												className={classNames("text-sm text-info font-weight-bold mb-1", mealViolation?.team > 0 && "cursor-pointer")}
												onClick={mealViolation?.team > 0
													? () => onClick("meal-timesheet-team")
													: undefined
												}
											>
												{t("check-team-timesheets", { count: mealViolation?.team || 0 })}{" "}
												<i className="fa-solid fa-greater-than text-xs ml-2" />
											</span>
										)}

									</div>
								</div>
							</div>
						)}

						{isTaskListSectionVisible(visibleLines.clockExceptions) && (
							<div className="d-flex flex-column border-bottom align py-2">
								<div className="d-flex m-0">
									<span>
										<i className="fa-solid fa-triangle-exclamation text-danger text-lg mr-3" />
									</span>
									<span className="mb-1">{t("clock-exceptions")}</span>
								</div>
								<div className="d-flex">
									<div style={{ width: "30px" }} />
									<div className="d-flex flex-column">
										{visibleLines.clockExceptions.total && (
											<span
												className={classNames("text-sm text-danger font-weight-bold mb-1", visibleExceptionsTotal > 0 && "cursor-pointer")}
												onClick={visibleExceptionsTotal > 0 ? () => onClick("clock-exceptions") : undefined}
											>
												{t("total-exceptions", { defaultValue: "Total exceptions" })}
												{" "}
												{visibleExceptionsTotal}
												<i className="fa-solid fa-greater-than text-xs ml-2" />
											</span>
										)}
										{exceptionRows.map(([key, label]) => {
											if (!visibleLines.clockExceptions[key]) {
												return null;
											}
											const count = clockExceptions[key];
											const isClickable = count > 0;
											return (
												<span
													key={key}
													className={classNames("text-sm text-dark font-weight-bold mb-1", isClickable && "cursor-pointer")}
													onClick={isClickable ? () => onClick(key) : undefined}
												>
													{t(label)}{" "}{count}
													<i className="fa-solid fa-greater-than text-xs ml-2" />
												</span>
											);
										})}
									</div>
								</div>
							</div>
						)}
					</CardBody>
				</>
			)}
		</Card>
	);
}

export default TaskList;
